import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DataManager } from '../../services/dataManager';
import VoiceDictationButton from '../common/VoiceDictationButton';
import html2pdf from 'html2pdf.js';

const PRESET_TECHNIQUES = [
    { id: 'giro_parola', label: 'Giro di Condivisione (Check-in/out)', icon: '⭕' },
    { id: 'respirazione', label: 'Respirazione, Centratura & Grounding', icon: '🌬️' },
    { id: 'carte_creative', label: 'Carte Creative & Proiettive', icon: '🎴' },
    { id: 'sedia_vuota', label: 'Sedia Vuota & Dialogo Polare', icon: '🪑' },
    { id: 'role_playing', label: 'Role Playing & Psicodramma', icon: '🎭' },
    { id: 'somatico', label: 'Esperimento Somatico & Bioenergetica', icon: '🧘' },
    { id: 'scrittura', label: 'Scrittura Espressiva & Journaling', icon: '📝' },
    { id: 'arte', label: 'Disegno & Arte Espressiva', icon: '🎨' },
    { id: 'coppie', label: 'Lavoro a Coppie / Sub-gruppi', icon: '👥' },
    { id: 'sogno_gruppo', label: 'Lavoro Onirico di Gruppo', icon: '🌙' },
    { id: 'feedback', label: 'Rispecchiamento & Feedback Circolare', icon: '🔄' },
    { id: 'scultura', label: 'Scultura Corporea di Gruppo', icon: '👁️' }
];

const PRESET_LOCATIONS = [
    'Presenza - Studio',
    'Presenza - Sede Associazione / Sala',
    'Online - Zoom',
    'Online - Google Meet',
    'All\'aperto / Residenziale'
];

const PRESET_CLIMATES = [
    { label: 'Accogliente & Aperto', icon: '🌿', color: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
    { label: 'Intenso & Emozionale', icon: '🌊', color: 'bg-cyan-50 text-cyan-800 border-cyan-200' },
    { label: 'Riflessivo & Introspettivo', icon: '🧘', color: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
    { label: 'Cauto & Resistente', icon: '🛡️', color: 'bg-amber-50 text-amber-800 border-amber-200' },
    { label: 'Confronto & Tensione', icon: '⚡', color: 'bg-rose-50 text-rose-800 border-rose-200' },
    { label: 'Coeso & Trasformativo', icon: '✨', color: 'bg-purple-50 text-purple-800 border-purple-200' }
];

const PRESET_PHASES = [
    '1. Apertura / Orientamento & Contratto',
    '2. Confronto / Differenziazione & Resistenze',
    '3. Coesione / Fiducia & Intimità',
    '4. Lavoro Profondo / Elaborazione',
    '5. Integrazione / Chiusura & Restituzione'
];

const PRESET_AVATARS = [
    { emoji: '👥', label: 'Gruppo Base' },
    { emoji: '🪞', label: 'Specchio / Consapevolezza' },
    { emoji: '🌿', label: 'Germoglio & Crescita' },
    { emoji: '🧭', label: 'Bussola / Direzione' },
    { emoji: '🌊', label: 'Oceano / Flusso' },
    { emoji: '🧘', label: 'Centratura & Presenza' },
    { emoji: '🎨', label: 'Creatività & Espressione' },
    { emoji: '💡', label: 'Insight & Chiarezza' },
    { emoji: '🌳', label: 'Albero & Radici' },
    { emoji: '🦉', label: 'Saggezza' },
    { emoji: '🛡️', label: 'Protezione & Confini' },
    { emoji: '⛵', label: 'Viaggio Condiviso' },
    { emoji: '☀️', label: 'Energia & Sole' },
    { emoji: '🎴', label: 'Carte Creative' },
    { emoji: '🕊️', label: 'Pace & Ascolto' },
    { emoji: '🔮', label: 'Trasformazione' },
    { emoji: '🏢', label: 'Ente / Istituzione' }
];

const GroupCounselingModal = ({ clients = [], counselor = null, counselorsList = [], onClose }) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'create' | 'edit' | 'detail'
    const [selectedReport, setSelectedReport] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [hasDraft, setHasDraft] = useState(false);
    
    // Group Report Modal State
    const [groupReportData, setGroupReportData] = useState(null);
    const [copiedGroupReport, setCopiedGroupReport] = useState(false);
    const groupReportDocRef = useRef(null);

    // Quick Group Avatar Picker in List View
    const [avatarPickerGroup, setAvatarPickerGroup] = useState(null);

    const printDocRef = useRef(null);

    const defaultCounselorName = useMemo(() => {
        return counselor?.name || (counselor?.email ? counselor.email.split('@')[0] : '');
    }, [counselor]);

    // Initial Form State
    const getInitialFormData = (isSingle = false) => ({
        id: null,
        meetingCategory: isSingle ? 'single' : 'group',
        isSingleMeeting: isSingle,
        title: isSingle ? 'Incontro Conoscitivo' : '',
        sessionNumber: isSingle ? 'Incontro Singolo' : '',
        groupAvatar: isSingle ? '🤝' : '👥',
        isEntity: false,
        entityName: '',
        entityReferent: '',
        entityNotes: '',
        groupObjective: '',
        date: new Date().toISOString().split('T')[0],
        duration: '90 min',
        location: 'Presenza - Studio',
        customLocation: '',
        counselorName: defaultCounselorName,
        coCounselor: '',
        theme: '',
        objective: '',
        climate: '',
        groupPhase: '',
        selectedClientIds: [],
        externalParticipants: '',
        absentParticipants: '',
        emergedThemes: '',
        techniques: [],
        customTechnique: '',
        insights: '',
        homework: '',
        nextSessionDate: '',
        nextSessionFocus: '',
        counselorNotes: ''
    });

    const [formData, setFormData] = useState(getInitialFormData);

    useEffect(() => {
        loadReports();
        const savedDraft = localStorage.getItem('draft_group_report');
        if (savedDraft) {
            setHasDraft(true);
        }
    }, []);

    const loadReports = async () => {
        try {
            setLoading(true);
            const data = await DataManager.getAllGroupReports();
            setReports(data || []);
        } catch (error) {
            console.error("Errore caricamento report di gruppo:", error);
        } finally {
            setLoading(false);
        }
    };

    // Auto-save draft in localStorage
    useEffect(() => {
        if (viewMode === 'create') {
            const hasContent = formData.title || formData.theme || formData.emergedThemes || formData.homework || formData.counselorNotes || formData.insights;
            if (hasContent) {
                localStorage.setItem('draft_group_report', JSON.stringify(formData));
                setHasDraft(true);
            }
        }
    }, [formData, viewMode]);

    const handleOpenCreate = (restoreDraft = false, isSingle = false) => {
        if (restoreDraft) {
            const savedDraft = localStorage.getItem('draft_group_report');
            if (savedDraft) {
                try {
                    const parsed = JSON.parse(savedDraft);
                    setFormData(parsed);
                    setViewMode('create');
                    return;
                } catch (e) {
                    console.error("Errore recupero bozza:", e);
                }
            }
        }

        setFormData(getInitialFormData(isSingle));
        setViewMode('create');
    };

    const handleClearDraft = () => {
        localStorage.removeItem('draft_group_report');
        setHasDraft(false);
        setFormData(getInitialFormData());
    };

    const handleOpenEdit = (report) => {
        setFormData({
            id: report.id,
            meetingCategory: report.meetingCategory || (report.isSingleMeeting ? 'single' : 'group'),
            isSingleMeeting: report.isSingleMeeting || report.meetingCategory === 'single',
            title: report.title || '',
            sessionNumber: report.sessionNumber || '',
            groupAvatar: report.groupAvatar || '👥',
            isEntity: Boolean(report.isEntity),
            entityName: report.entityName || '',
            entityReferent: report.entityReferent || '',
            entityNotes: report.entityNotes || '',
            groupObjective: report.groupObjective || '',
            date: report.date || new Date().toISOString().split('T')[0],
            duration: report.duration || '90 min',
            location: report.location || 'Presenza - Studio',
            customLocation: report.customLocation || '',
            counselorName: report.counselorName || defaultCounselorName,
            coCounselor: report.coCounselor || '',
            theme: report.theme || '',
            objective: report.objective || '',
            climate: report.climate || '',
            groupPhase: report.groupPhase || '',
            selectedClientIds: report.selectedClientIds || [],
            externalParticipants: report.externalParticipants || '',
            absentParticipants: report.absentParticipants || '',
            emergedThemes: report.emergedThemes || '',
            techniques: report.techniques || [],
            customTechnique: report.customTechnique || '',
            insights: report.insights || '',
            homework: report.homework || '',
            nextSessionDate: report.nextSessionDate || '',
            nextSessionFocus: report.nextSessionFocus || '',
            counselorNotes: report.counselorNotes || ''
        });
        setViewMode('edit');
    };

    const handleOpenDetail = (report) => {
        setSelectedReport(report);
        setViewMode('detail');
    };

    const toggleClientSelection = (clientId) => {
        setFormData(prev => {
            const currentClients = prev.selectedClientIds || [];
            const exists = currentClients.includes(clientId);
            return {
                ...prev,
                selectedClientIds: exists
                    ? currentClients.filter(id => id !== clientId)
                    : [...currentClients, clientId]
            };
        });
    };

    const toggleTechnique = (techLabel) => {
        setFormData(prev => {
            const currentTech = prev.techniques || [];
            const exists = currentTech.includes(techLabel);
            return {
                ...prev,
                techniques: exists
                    ? currentTech.filter(t => t !== techLabel)
                    : [...currentTech, techLabel]
            };
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.title.trim() && !formData.theme.trim()) {
            alert("Inserisci almeno il nome del gruppo o il tema dell'incontro.");
            return;
        }

        setIsSaving(true);
        try {
            const effectiveLocation = formData.location === 'Altro...'
                ? (formData.customLocation.trim() || 'Altro')
                : formData.location;

            const isSingle = formData.meetingCategory === 'single' || formData.isSingleMeeting;
            const payload = {
                meetingCategory: isSingle ? 'single' : 'group',
                isSingleMeeting: isSingle,
                title: formData.title.trim() || (isSingle ? 'Incontro Conoscitivo' : 'Percorso di Gruppo'),
                sessionNumber: isSingle ? (formData.sessionNumber.trim() || 'Incontro Singolo') : formData.sessionNumber.trim(),
                groupAvatar: formData.groupAvatar || (isSingle ? '🤝' : '👥'),
                isEntity: Boolean(formData.isEntity),
                entityName: formData.isEntity ? (formData.entityName || '').trim() : '',
                entityReferent: formData.isEntity ? (formData.entityReferent || '').trim() : '',
                entityNotes: formData.isEntity ? (formData.entityNotes || '').trim() : '',
                groupObjective: (formData.groupObjective || '').trim(),
                date: formData.date,
                duration: formData.duration.trim(),
                location: effectiveLocation,
                customLocation: formData.customLocation.trim(),
                counselorName: formData.counselorName.trim() || defaultCounselorName,
                coCounselor: formData.coCounselor.trim(),
                theme: formData.theme.trim(),
                objective: formData.objective.trim(),
                climate: formData.climate,
                groupPhase: formData.groupPhase,
                selectedClientIds: formData.selectedClientIds || [],
                externalParticipants: formData.externalParticipants.trim(),
                absentParticipants: formData.absentParticipants.trim(),
                emergedThemes: formData.emergedThemes.trim(),
                techniques: formData.techniques || [],
                customTechnique: formData.customTechnique.trim(),
                insights: formData.insights.trim(),
                homework: formData.homework.trim(),
                nextSessionDate: formData.nextSessionDate,
                nextSessionFocus: formData.nextSessionFocus.trim(),
                counselorNotes: formData.counselorNotes.trim()
            };

            if (formData.id) {
                await DataManager.updateGroupReport(formData.id, payload);
            } else {
                await DataManager.saveGroupReport(payload);
                localStorage.removeItem('draft_group_report');
                setHasDraft(false);
            }

            await loadReports();
            setViewMode('list');
        } catch (error) {
            console.error("Errore salvataggio report gruppo:", error);
            alert("Errore durante il salvataggio del report. I dati sono comunque stati salvati in locale.");
            await loadReports();
            setViewMode('list');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (reportId) => {
        if (!window.confirm("Sei sicuro di voler eliminare questo report di gruppo?")) return;
        try {
            await DataManager.deleteGroupReport(reportId);
            await loadReports();
            if (viewMode === 'detail' || viewMode === 'edit') {
                setViewMode('list');
            }
        } catch (error) {
            console.error("Errore eliminazione report gruppo:", error);
            alert("Errore durante l'eliminazione del report.");
        }
    };

    const getParticipantNames = (report) => {
        const clientNames = (report.selectedClientIds || [])
            .map(id => clients.find(c => c.id === id)?.name)
            .filter(Boolean);
        
        if (report.externalParticipants) {
            const extras = report.externalParticipants.split(',').map(s => s.trim()).filter(Boolean);
            clientNames.push(...extras);
        }

        return clientNames;
    };

    const handleCopyReport = async (report) => {
        const participants = getParticipantNames(report).join(', ') || 'Nessun partecipante registrato';
        const dateStr = new Date(report.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
        const techniquesStr = [...(report.techniques || []), report.customTechnique].filter(Boolean).join(', ') || 'Nessuna tecnica indicata';

        const text = `
=====================================================
📋 REPORT COUNSELING DI GRUPPO - AWARENESSTOOL
=====================================================
GRUPPO: ${report.title || 'Incontro di Gruppo'} ${report.sessionNumber ? `(Sessione: ${report.sessionNumber})` : ''}
DATA: ${dateStr} | DURATA: ${report.duration || 'N/D'}
LUOGO: ${report.location || 'N/D'}
COUNSELOR CONDUTTORE: ${report.counselorName || 'N/D'} ${report.coCounselor ? `| CO-CONDUTTORE: ${report.coCounselor}` : ''}
-----------------------------------------------------
🎯 TEMA DELL'INCONTRO:
${report.theme || 'N/D'}

🎯 OBIETTIVO DELLA SESSIONE:
${report.objective || 'Non specificato'}

👥 PARTECIPANTI:
${participants}
${report.absentParticipants ? `ASSENTI: ${report.absentParticipants}` : ''}

🌡️ CLIMA EMOTIVO & FASE DEL GRUPPO:
Clima: ${report.climate || 'N/D'} | Fase: ${report.groupPhase || 'N/D'}
-----------------------------------------------------
✨ TEMI EMERSI & DINAMICHE DI GRUPPO:
${report.emergedThemes || 'Nessuna dinamica registrata'}

🛠️ TECNICHE E STRUMENTI UTILIZZATI:
${techniquesStr}

💡 INSIGHT & PUNTI DI SVOLTA:
${report.insights || 'Nessun insight registrato'}

🏠 EVENTUALI ESERCIZI A CASA (HOMEWORK):
${report.homework || 'Nessun esercizio assegnato'}

📅 PROSSIMO INCONTRO:
${report.nextSessionDate ? `Data: ${report.nextSessionDate} - Focus: ${report.nextSessionFocus || 'N/D'}` : 'Non programmato'}

🧘 NOTE E VISSUTO DEL COUNSELOR (RISERVATO):
${report.counselorNotes || 'Nessuna nota registrata'}
=====================================================
`.trim();

        try {
            await navigator.clipboard.writeText(text);
            setCopiedId(report.id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            console.error("Errore copia report:", err);
        }
    };

    const handleDownloadPDF = async (report) => {
        if (!printDocRef.current || downloading) return;
        setDownloading(true);
        try {
            const element = printDocRef.current;
            const cleanTitle = (report.title || 'Incontro').replace(/[^a-zA-Z0-9_-]/g, '_');
            const filename = `Report_Gruppo_${cleanTitle}_${report.date}.pdf`;
            const opt = {
                margin: [8, 8, 8, 8],
                filename: filename,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };
            await html2pdf().set(opt).from(element).save();
        } catch (err) {
            console.error("Errore esportazione PDF:", err);
            window.print();
        } finally {
            setDownloading(false);
        }
    };

    const DEFAULT_GROUP_OBJECTIVE = "Il counseling di gruppo offre uno spazio protetto e condiviso di esplorazione, confronto relazionale e consapevolezza, volto ad accrescere l'autonomia, l'ascolto di sé e le risorse personali attraverso la risonanza del gruppo.";

    const handleSelectGroupAvatar = async (groupTitle, newAvatar) => {
        try {
            await DataManager.updateGroupAvatar(groupTitle, newAvatar);
            setAvatarPickerGroup(null);
            await loadReports();
        } catch (err) {
            console.error("Errore aggiornamento avatar:", err);
        }
    };

    const handleOpenGroupReport = (groupName, groupMeetings) => {
        const avatar = groupMeetings[0]?.groupAvatar || '👥';
        const customObjective = groupMeetings.find(m => m.groupObjective && m.groupObjective.trim())?.groupObjective;
        const objective = customObjective || DEFAULT_GROUP_OBJECTIVE;

        const entityMeeting = groupMeetings.find(m => m.isEntity);
        const entityInfo = entityMeeting ? {
            isEntity: true,
            entityName: entityMeeting.entityName,
            entityReferent: entityMeeting.entityReferent,
            entityNotes: entityMeeting.entityNotes
        } : null;

        const allParticipantsMap = new Map();
        groupMeetings.forEach(meeting => {
            const pNames = getParticipantNames(meeting);
            pNames.forEach(name => {
                if (!allParticipantsMap.has(name)) {
                    allParticipantsMap.set(name, {
                        name,
                        attendedCount: 0,
                        meetingAttendance: []
                    });
                }
            });
        });

        allParticipantsMap.forEach((data, pName) => {
            groupMeetings.forEach((m, idx) => {
                const pNames = getParticipantNames(m);
                const present = pNames.includes(pName);
                if (present) data.attendedCount += 1;
                data.meetingAttendance.push({
                    meetingIndex: idx + 1,
                    sessionNumber: m.sessionNumber || `Incontro ${idx + 1}`,
                    date: m.date,
                    present
                });
            });
        });

        const participantsAttendance = Array.from(allParticipantsMap.values()).map(p => ({
            ...p,
            total: groupMeetings.length,
            percentage: Math.round((p.attendedCount / groupMeetings.length) * 100)
        })).sort((a, b) => b.attendedCount - a.attendedCount);

        const climates = groupMeetings.map(m => m.climate).filter(Boolean);
        const phases = groupMeetings.map(m => m.groupPhase).filter(Boolean);

        setGroupReportData({
            groupName,
            avatar,
            objective,
            isDefaultObjective: !customObjective,
            entityInfo,
            totalMeetings: groupMeetings.length,
            groupMeetings,
            participantsAttendance,
            climates,
            phases
        });
    };

    const handleCopyGroupReport = async () => {
        if (!groupReportData) return;
        const attList = groupReportData.participantsAttendance
            .map(p => `• ${p.name}: ${p.attendedCount}/${groupReportData.totalMeetings} incontri (${p.percentage}%)`)
            .join('\n');

        const text = `
=====================================================
📊 BILANCIO & REPORT DI GRUPPO - AWARENESSTOOL
=====================================================
GRUPPO: ${groupReportData.groupName}
${groupReportData.entityInfo ? `ENTE COMMITTENTE: ${groupReportData.entityInfo.entityName} ${groupReportData.entityInfo.entityReferent ? `(Referente: ${groupReportData.entityInfo.entityReferent})` : ''}` : ''}
TOTALE INCONTRI SVOLTI: ${groupReportData.totalMeetings}
-----------------------------------------------------
🎯 OBIETTIVO DEL PERCORSO:
${groupReportData.objective}

👥 PRESENZE E PARTECIPAZIONE:
${attList || 'Nessun partecipante registrato'}

🌡️ CLIMA PREVALENTE:
${groupReportData.climates.length > 0 ? [...new Set(groupReportData.climates)].join(', ') : 'Non specificato'}

📅 CRONISTORIA DEGLI INCONTRI:
${groupReportData.groupMeetings.map((m, i) => {
    const dStr = new Date(m.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
    return `[${dStr}] ${m.sessionNumber || `Incontro ${i + 1}`}: ${m.theme || 'Nessun tema'}${m.insights ? `\n   ↳ Insight: ${m.insights}` : ''}`;
}).join('\n')}
=====================================================
`.trim();

        try {
            await navigator.clipboard.writeText(text);
            setCopiedGroupReport(true);
            setTimeout(() => setCopiedGroupReport(false), 2000);
        } catch (err) {
            console.error("Errore copia bilancio gruppo:", err);
        }
    };

    const handleDownloadGroupReportPDF = async () => {
        if (!groupReportDocRef.current || downloading) return;
        setDownloading(true);
        try {
            const element = groupReportDocRef.current;
            const cleanTitle = (groupReportData.groupName || 'Gruppo').replace(/[^a-zA-Z0-9_-]/g, '_');
            const filename = `Bilancio_Gruppo_${cleanTitle}.pdf`;
            const opt = {
                margin: [8, 8, 8, 8],
                filename: filename,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };
            await html2pdf().set(opt).from(element).save();
        } catch (err) {
            console.error("Errore PDF report gruppo:", err);
            window.print();
        } finally {
            setDownloading(false);
        }
    };

    const filteredReports = useMemo(() => {
        if (!searchTerm) return reports;
        const q = searchTerm.toLowerCase();
        return reports.filter(r => {
            const title = (r.title || '').toLowerCase();
            const theme = (r.theme || '').toLowerCase();
            const emerged = (r.emergedThemes || '').toLowerCase();
            const counselorName = (r.counselorName || '').toLowerCase();
            const location = (r.location || '').toLowerCase();
            const parts = getParticipantNames(r).join(' ').toLowerCase();
            return title.includes(q) || theme.includes(q) || emerged.includes(q) || parts.includes(q) || counselorName.includes(q) || location.includes(q);
        });
    }, [reports, searchTerm, clients]);

    // Raggruppamento per Gruppi Strutturati e Incontri Singoli/Conoscitivi
    const { structuredGroups, singleReports } = useMemo(() => {
        const groups = {};
        const singles = [];

        filteredReports.forEach(report => {
            const isSingle = report.meetingCategory === 'single' || 
                             report.isSingleMeeting || 
                             (report.title && (
                                report.title.toLowerCase().includes('conoscitivo') || 
                                report.title.toLowerCase().includes('singolo') || 
                                report.title.toLowerCase() === 'senza gruppo'
                             ));
            
            if (isSingle) {
                singles.push(report);
            } else {
                const groupName = (report.title || 'Incontro di Gruppo').trim();
                if (!groups[groupName]) {
                    groups[groupName] = [];
                }
                groups[groupName].push(report);
            }
        });

        // Ordina gli incontri di ogni gruppo in ordine cronologico / sessione
        Object.keys(groups).forEach(gName => {
            groups[gName].sort((a, b) => {
                const sA = parseInt((a.sessionNumber || '').replace(/\D/g, ''), 10);
                const sB = parseInt((b.sessionNumber || '').replace(/\D/g, ''), 10);
                if (!isNaN(sA) && !isNaN(sB) && sA !== sB) return sA - sB;
                return new Date(a.date) - new Date(b.date);
            });
        });

        // Ordina gli incontri singoli dal più recente
        singles.sort((a, b) => new Date(b.date) - new Date(a.date));

        return { structuredGroups: groups, singleReports: singles };
    }, [filteredReports]);

    return (
        <div className="fixed inset-0 bg-slate-900/80 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 backdrop-blur-md animate-fade-in print:p-0 print:bg-white print:fixed">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[94vh] flex flex-col overflow-hidden border border-slate-200 print:max-h-none print:shadow-none print:border-none print:rounded-none">
                
                {/* Header coerente con la palette AwarenessTool */}
                <div 
                    style={{ background: 'linear-gradient(135deg, #1e3847 0%, #24485d 50%, #335d73 100%)', backgroundColor: '#24485d' }}
                    className="text-white p-5 sm:p-6 border-b border-[#335d73] flex items-center justify-between print:hidden"
                >
                    <div className="flex items-center gap-3.5">
                        <div 
                            style={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', borderColor: 'rgba(255, 255, 255, 0.25)' }}
                            className="w-12 h-12 rounded-2xl border flex items-center justify-center text-2xl shadow-inner"
                        >
                            👥
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl font-black tracking-tight text-white">Counseling di Gruppo</h2>
                                <span 
                                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.35)', color: '#ffffff' }}
                                    className="text-[10px] font-bold px-2.5 py-0.5 rounded-full border uppercase tracking-widest"
                                >
                                    REPORT DI GRUPPO
                                </span>
                            </div>
                            <p className="text-slate-100 text-xs sm:text-sm font-normal mt-0.5">
                                Spazio condiviso, dinamiche relazionali, tecniche ed evoluzione degli incontri
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        {viewMode === 'list' ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => handleOpenCreate(false, true)}
                                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                                    className="px-3.5 py-2.5 rounded-xl text-white text-xs font-bold transition flex items-center gap-1.5 border hover:bg-white/25 active:scale-95"
                                >
                                    <span>🤝</span>
                                    <span>+ Incontro Singolo / Conoscitivo</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleOpenCreate(false, false)}
                                    style={{ backgroundColor: '#ffffff', color: '#1e3847' }}
                                    className="px-4 py-2.5 rounded-xl font-bold text-xs transition flex items-center gap-1.5 shadow-md hover:bg-slate-100 active:scale-95"
                                >
                                    <span>👥</span>
                                    <span>+ Nuovo Gruppo</span>
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setViewMode('list')}
                                style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                                className="px-3.5 py-2.5 rounded-xl text-white text-xs font-semibold transition flex items-center gap-1.5 border hover:bg-white/25"
                            >
                                <span>←</span>
                                <span>Torna all'Elenco</span>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                            className="w-9 h-9 rounded-xl text-white flex items-center justify-center transition border hover:bg-white/25 ml-1"
                            title="Chiudi finestra"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* Body Content by ViewMode */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50/70 print:p-0 print:bg-white">
                    
                    {/* VIEW: LIST OF REPORTS */}
                    {viewMode === 'list' && (
                        <div className="space-y-5">
                            {/* Draft Notification Banner */}
                            {hasDraft && (
                                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 sm:p-4 flex flex-wrap items-center justify-between gap-3 text-amber-900 shadow-sm">
                                    <div className="flex items-center gap-2.5">
                                        <span className="text-xl">📝</span>
                                        <div>
                                            <p className="text-xs font-bold">Hai una bozza non salvata</p>
                                            <p className="text-[11px] text-amber-700">È presente un report iniziato in precedenza con i dati salvati automaticamente.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleOpenCreate(true)}
                                            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
                                        >
                                            Ripristina Bozza
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleClearDraft}
                                            className="px-3 py-1.5 bg-white hover:bg-amber-100 text-amber-800 border border-amber-300 rounded-xl text-xs font-semibold transition"
                                        >
                                            Svuota
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Search & Stats Bar */}
                            <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                                <div className="flex-1 min-w-[240px]">
                                    <input
                                        type="text"
                                        placeholder="🔍 Cerca per tema, counselor, luogo, titolo gruppo o partecipante..."
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                    />
                                </div>
                                <div className="text-xs text-[#24485d] font-bold bg-[#f0f5f8] px-3.5 py-1.5 rounded-xl border border-[#d1dde5]">
                                    {Object.keys(structuredGroups).length} {Object.keys(structuredGroups).length === 1 ? 'gruppo' : 'gruppi'} • {singleReports.length} {singleReports.length === 1 ? 'singolo/conoscitivo' : 'singoli/conoscitivi'} • {reports.length} totali
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center py-20 text-slate-400">
                                    <div className="animate-spin text-4xl mb-3">⏳</div>
                                    <p className="text-sm font-semibold text-slate-600">Caricamento incontri in corso...</p>
                                </div>
                            ) : (Object.keys(structuredGroups).length === 0 && singleReports.length === 0) ? (
                                <div className="text-center py-16 bg-white rounded-3xl border border-dashed border-slate-300 p-8 shadow-sm">
                                    <div className="w-16 h-16 bg-[#f0f5f8] text-[#24485d] rounded-3xl flex items-center justify-center text-3xl mx-auto mb-3 shadow-inner border border-[#d1dde5]">
                                        👥
                                    </div>
                                    <h4 className="text-base font-bold text-slate-800">Nessun gruppo o incontro registrato</h4>
                                    <p className="text-xs text-slate-500 mt-1.5 max-w-md mx-auto leading-relaxed">
                                        Puoi creare un <strong>percorso di gruppo continuativo</strong> (con più incontri ordinati in sequenza) oppure registrare un <strong>incontro singolo o conoscitivo</strong>.
                                    </p>
                                    <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
                                        <button
                                            type="button"
                                            onClick={() => handleOpenCreate(false, false)}
                                            style={{ backgroundColor: '#335d73' }}
                                            className="px-5 py-2.5 rounded-xl hover:bg-[#24485d] text-white text-xs font-bold transition shadow-md active:scale-95 flex items-center gap-1.5"
                                        >
                                            <span>👥</span>
                                            <span>+ Crea Primo Gruppo</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleOpenCreate(false, true)}
                                            className="px-4 py-2.5 rounded-xl bg-white hover:bg-slate-100 text-[#24485d] border border-[#d1dde5] text-xs font-bold transition shadow-sm active:scale-95 flex items-center gap-1.5"
                                        >
                                            <span>🤝</span>
                                            <span>+ Incontro Singolo / Conoscitivo</span>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* 1. SEZIONE GRUPPI STRUTTURATI - INCONTRI IN FILA */}
                                    {Object.entries(structuredGroups).map(([groupName, groupMeetings]) => {
                                        const allGroupParticipants = [...new Set(groupMeetings.flatMap(r => getParticipantNames(r)))];
                                        const groupAvatar = groupMeetings[0]?.groupAvatar || '👥';
                                        const entityMeeting = groupMeetings.find(m => m.isEntity && m.entityName);

                                        return (
                                            <div key={groupName} className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-5 space-y-4">
                                                {/* Header del Gruppo */}
                                                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3.5">
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={() => setAvatarPickerGroup({ groupName, currentAvatar: groupAvatar })}
                                                            className="w-12 h-12 rounded-2xl bg-[#f0f5f8] hover:bg-[#e2edf2] border border-[#d1dde5] text-[#24485d] flex items-center justify-center text-2xl font-bold shadow-inner relative group transition"
                                                            title="Clicca per cambiare l'icona/immagine del gruppo"
                                                        >
                                                            <span>{groupAvatar}</span>
                                                            <span className="absolute -bottom-1 -right-1 bg-[#24485d] text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center shadow">
                                                                ✏️
                                                            </span>
                                                        </button>
                                                        <div>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <h3 className="text-base sm:text-lg font-black text-[#24485d]">
                                                                    {groupName}
                                                                </h3>
                                                                <span className="text-[11px] font-bold text-[#335d73] bg-[#f0f5f8] border border-[#d1dde5] px-2.5 py-0.5 rounded-full">
                                                                    {groupMeetings.length} {groupMeetings.length === 1 ? 'incontro' : 'incontri in sequenza'}
                                                                </span>
                                                                {entityMeeting && (
                                                                    <span className="text-[11px] font-bold text-amber-900 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                                                                        <span>🏢</span>
                                                                        <span>Ente: {entityMeeting.entityName}</span>
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {allGroupParticipants.length > 0 && (
                                                                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">
                                                                    <span className="font-semibold text-slate-600">Partecipanti del gruppo ({allGroupParticipants.length}):</span>
                                                                    {allGroupParticipants.map((pName, pIdx) => (
                                                                        <span key={pIdx} className="bg-slate-100 text-slate-700 text-[11px] font-medium px-2 py-0.5 rounded-md border border-slate-200">
                                                                            👤 {pName}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleOpenGroupReport(groupName, groupMeetings)}
                                                            className="px-3.5 py-2 rounded-xl bg-[#24485d] hover:bg-[#1e3847] text-white text-xs font-bold transition flex items-center gap-1.5 shadow-sm active:scale-95"
                                                            title="Visualizza il report generale del gruppo, presenze e mood"
                                                        >
                                                            <span>📊</span>
                                                            <span>Bilancio & Report di Gruppo</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const lastReport = groupMeetings[groupMeetings.length - 1];
                                                                const nextSessNum = groupMeetings.length + 1;
                                                                setFormData({
                                                                    ...getInitialFormData(false),
                                                                    title: groupName,
                                                                    groupAvatar: lastReport?.groupAvatar || groupAvatar,
                                                                    isEntity: Boolean(lastReport?.isEntity),
                                                                    entityName: lastReport?.entityName || '',
                                                                    entityReferent: lastReport?.entityReferent || '',
                                                                    entityNotes: lastReport?.entityNotes || '',
                                                                    groupObjective: lastReport?.groupObjective || '',
                                                                    sessionNumber: `Incontro ${nextSessNum}`,
                                                                    selectedClientIds: lastReport?.selectedClientIds || [],
                                                                    externalParticipants: lastReport?.externalParticipants || '',
                                                                    location: lastReport?.location || 'Presenza - Studio',
                                                                    customLocation: lastReport?.customLocation || '',
                                                                    counselorName: lastReport?.counselorName || defaultCounselorName
                                                                });
                                                                setViewMode('create');
                                                            }}
                                                            className="px-3.5 py-2 rounded-xl bg-[#f0f5f8] hover:bg-[#e2edf2] text-[#24485d] border border-[#d1dde5] text-xs font-bold transition flex items-center gap-1.5 shadow-sm"
                                                        >
                                                            <span>+</span>
                                                            <span>Aggiungi Incontro</span>
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Incontri in fila per questo gruppo */}
                                                <div className="space-y-3">
                                                    {groupMeetings.map((report, rIdx) => {
                                                        const participants = getParticipantNames(report);
                                                        const dateStr = new Date(report.date).toLocaleDateString('it-IT', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        });

                                                        return (
                                                            <div
                                                                key={report.id}
                                                                className="bg-slate-50/60 hover:bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 hover:border-[#5c8aa8] hover:shadow-md transition flex flex-col justify-between"
                                                            >
                                                                <div>
                                                                    <div className="flex justify-between items-start gap-2 border-b border-slate-200/60 pb-2.5 mb-2.5">
                                                                        <div>
                                                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                                                <span className="text-[10px] font-black uppercase tracking-wider text-[#24485d] bg-[#f0f5f8] px-2.5 py-0.5 rounded-md border border-[#d1dde5]">
                                                                                    📅 {dateStr}
                                                                                </span>
                                                                                {report.sessionNumber ? (
                                                                                    <span className="text-[10px] font-bold text-[#335d73] bg-white px-2.5 py-0.5 rounded-md border border-slate-200">
                                                                                        {report.sessionNumber.startsWith('Sess') || report.sessionNumber.startsWith('Incontro') ? report.sessionNumber : `Sess. ${report.sessionNumber}`}
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="text-[10px] font-bold text-slate-500 bg-white px-2 py-0.5 rounded-md border border-slate-200">
                                                                                        Incontro #{rIdx + 1}
                                                                                    </span>
                                                                                )}
                                                                                {report.location && (
                                                                                    <span className="text-[10px] font-medium text-slate-500">
                                                                                        📍 {report.location}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            {report.counselorName && (
                                                                                <p className="text-[11px] text-slate-500 font-medium">
                                                                                    👤 Counselor: <strong className="text-slate-700">{report.counselorName}</strong>
                                                                                    {report.coCounselor && <span className="text-slate-400"> (Co-conduttore: {report.coCounselor})</span>}
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex items-center gap-1">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleCopyReport(report)}
                                                                                className="text-xs text-slate-400 hover:text-[#335d73] p-1.5 rounded-lg hover:bg-slate-100 transition"
                                                                                title="Copia report formattato"
                                                                            >
                                                                                {copiedId === report.id ? '✓' : '📋'}
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleOpenEdit(report)}
                                                                                className="text-xs text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-100 transition"
                                                                                title="Modifica report"
                                                                            >
                                                                                ✏️
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleDelete(report.id)}
                                                                                className="text-xs text-slate-400 hover:text-rose-600 p-1.5 rounded-lg hover:bg-slate-100 transition"
                                                                                title="Elimina report"
                                                                            >
                                                                                🗑️
                                                                            </button>
                                                                        </div>
                                                                    </div>

                                                                    {/* Theme & Objective */}
                                                                    {report.theme && (
                                                                        <div className="mb-2">
                                                                            <p className="text-xs font-bold text-[#24485d] bg-[#f0f5f8] px-3 py-1.5 rounded-xl border border-[#d1dde5]">
                                                                                🎯 Tema: <span className="font-normal text-slate-800">{report.theme}</span>
                                                                            </p>
                                                                        </div>
                                                                    )}

                                                                    {/* Climate badge if set */}
                                                                    {report.climate && (
                                                                        <div className="mb-2">
                                                                            <span className="text-[11px] font-semibold bg-white text-slate-700 px-2.5 py-0.5 rounded-lg border border-slate-200 inline-flex items-center gap-1">
                                                                                🌡️ Clima: {report.climate}
                                                                            </span>
                                                                        </div>
                                                                    )}

                                                                    {/* Participants of this encounter */}
                                                                    {participants.length > 0 && (
                                                                        <div className="mb-2">
                                                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                                                                            Presenti ({participants.length}):
                                                                            </span>
                                                                            <div className="flex flex-wrap gap-1">
                                                                                {participants.map((name, i) => (
                                                                                    <span key={i} className="text-[11px] bg-white text-slate-700 font-medium px-2 py-0.5 rounded-lg border border-slate-200">
                                                                                        👤 {name}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Emerged themes snippet */}
                                                                    {report.emergedThemes && (
                                                                        <p className="text-xs text-slate-600 italic line-clamp-2 mb-2 bg-white/70 p-2 rounded-lg border border-slate-100">
                                                                            "{report.emergedThemes}"
                                                                        </p>
                                                                    )}
                                                                </div>

                                                                <div className="pt-2.5 border-t border-slate-200/60 flex items-center justify-between mt-1">
                                                                    <span className="text-[11px] text-slate-400 font-medium">
                                                                        {(report.techniques || []).length} tecniche usate
                                                                    </span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleOpenDetail(report)}
                                                                        className="text-xs font-bold text-[#24485d] hover:text-[#335d73] bg-[#f0f5f8] hover:bg-[#e2edf2] px-3.5 py-1.5 rounded-xl border border-[#d1dde5] transition flex items-center gap-1"
                                                                    >
                                                                        <span>Visualizza Dettaglio</span>
                                                                        <span>→</span>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* 2. SEZIONE INCONTRI SINGOLI, CONOSCITIVI & WORKSHOP */}
                                    {singleReports.length > 0 && (
                                        <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-5 space-y-4">
                                            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3.5">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 flex items-center justify-center text-xl font-bold shadow-inner">
                                                        🤝
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <h3 className="text-base sm:text-lg font-black text-[#24485d]">
                                                                Incontri Singoli, Conoscitivi & Workshop
                                                            </h3>
                                                            <span className="text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-full">
                                                                {singleReports.length} {singleReports.length === 1 ? 'incontro' : 'incontri registrati'}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-slate-500 mt-0.5">
                                                            Incontri introduttivi, open day o sessioni singole non legate a un gruppo continuativo
                                                        </p>
                                                    </div>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenCreate(false, true)}
                                                    className="px-3.5 py-2 rounded-xl bg-[#f0f5f8] hover:bg-[#e2edf2] text-[#24485d] border border-[#d1dde5] text-xs font-bold transition flex items-center gap-1.5 shadow-sm"
                                                >
                                                    <span>+</span>
                                                    <span>Registra Incontro Singolo</span>
                                                </button>
                                            </div>

                                            {/* Elenco Incontri Singoli */}
                                            <div className="space-y-3">
                                                {singleReports.map((report, rIdx) => {
                                                    const participants = getParticipantNames(report);
                                                    const dateStr = new Date(report.date).toLocaleDateString('it-IT', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    });

                                                    return (
                                                        <div
                                                            key={report.id}
                                                            className="bg-slate-50/60 hover:bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 hover:border-[#5c8aa8] hover:shadow-md transition flex flex-col justify-between"
                                                        >
                                                            <div>
                                                                <div className="flex justify-between items-start gap-2 border-b border-slate-200/60 pb-2.5 mb-2.5">
                                                                    <div>
                                                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                                                            <span className="text-[10px] font-black uppercase tracking-wider text-amber-900 bg-amber-50 px-2.5 py-0.5 rounded-md border border-amber-200">
                                                                                🤝 {report.title || 'Incontro Singolo'}
                                                                            </span>
                                                                            <span className="text-[10px] font-black uppercase tracking-wider text-[#24485d] bg-[#f0f5f8] px-2.5 py-0.5 rounded-md border border-[#d1dde5]">
                                                                                📅 {dateStr}
                                                                            </span>
                                                                            {report.location && (
                                                                                <span className="text-[10px] font-medium text-slate-500">
                                                                                    📍 {report.location}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {report.counselorName && (
                                                                            <p className="text-[11px] text-slate-500 font-medium">
                                                                                👤 Counselor: <strong className="text-slate-700">{report.counselorName}</strong>
                                                                                {report.coCounselor && <span className="text-slate-400"> (Co-conduttore: {report.coCounselor})</span>}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center gap-1">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleCopyReport(report)}
                                                                            className="text-xs text-slate-400 hover:text-[#335d73] p-1.5 rounded-lg hover:bg-slate-100 transition"
                                                                            title="Copia report formattato"
                                                                        >
                                                                            {copiedId === report.id ? '✓' : '📋'}
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleOpenEdit(report)}
                                                                            className="text-xs text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-100 transition"
                                                                            title="Modifica report"
                                                                        >
                                                                            ✏️
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDelete(report.id)}
                                                                            className="text-xs text-slate-400 hover:text-rose-600 p-1.5 rounded-lg hover:bg-slate-100 transition"
                                                                            title="Elimina report"
                                                                        >
                                                                            🗑️
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {/* Theme & Objective */}
                                                                {report.theme && (
                                                                    <div className="mb-2">
                                                                        <p className="text-xs font-bold text-[#24485d] bg-[#f0f5f8] px-3 py-1.5 rounded-xl border border-[#d1dde5]">
                                                                            🎯 Tema / Obiettivo: <span className="font-normal text-slate-800">{report.theme}</span>
                                                                        </p>
                                                                    </div>
                                                                )}

                                                                {/* Climate badge if set */}
                                                                {report.climate && (
                                                                    <div className="mb-2">
                                                                        <span className="text-[11px] font-semibold bg-white text-slate-700 px-2.5 py-0.5 rounded-lg border border-slate-200 inline-flex items-center gap-1">
                                                                            🌡️ Clima: {report.climate}
                                                                        </span>
                                                                    </div>
                                                                )}

                                                                {/* Participants */}
                                                                {participants.length > 0 && (
                                                                    <div className="mb-2">
                                                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                                                                            Partecipanti ({participants.length}):
                                                                        </span>
                                                                        <div className="flex flex-wrap gap-1">
                                                                            {participants.map((name, i) => (
                                                                                <span key={i} className="text-[11px] bg-white text-slate-700 font-medium px-2 py-0.5 rounded-lg border border-slate-200">
                                                                                    👤 {name}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Emerged themes snippet */}
                                                                {report.emergedThemes && (
                                                                    <p className="text-xs text-slate-600 italic line-clamp-2 mb-2 bg-white/70 p-2 rounded-lg border border-slate-100">
                                                                        "{report.emergedThemes}"
                                                                    </p>
                                                                )}
                                                            </div>

                                                            <div className="pt-2.5 border-t border-slate-200/60 flex items-center justify-between mt-1">
                                                                <span className="text-[11px] text-slate-400 font-medium">
                                                                    {(report.techniques || []).length} tecniche usate
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleOpenDetail(report)}
                                                                    className="text-xs font-bold text-[#24485d] hover:text-[#335d73] bg-[#f0f5f8] hover:bg-[#e2edf2] px-3.5 py-1.5 rounded-xl border border-[#d1dde5] transition flex items-center gap-1"
                                                                >
                                                                    <span>Visualizza Dettaglio</span>
                                                                    <span>→</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* VIEW: CREATE / EDIT FORM */}
                    {(viewMode === 'create' || viewMode === 'edit') && (
                        <form onSubmit={handleSave} className="max-w-4xl mx-auto bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
                            <div className="border-b border-slate-200 pb-4 flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-black text-slate-900">
                                        {viewMode === 'edit' 
                                            ? (formData.meetingCategory === 'single' ? '✏️ Modifica Incontro Singolo / Conoscitivo' : '✏️ Modifica Incontro di Gruppo')
                                            : (formData.meetingCategory === 'single' ? '📝 Registra Incontro Singolo / Conoscitivo' : '📝 Nuovo Incontro di Gruppo')}
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        {formData.meetingCategory === 'single'
                                            ? "Compila i dettagli, i partecipanti e le dinamiche emerse durante l'incontro singolo o conoscitivo."
                                            : "Compila i dati organizzativi, le dinamiche e gli elementi metodologici dell'incontro di gruppo."}
                                    </p>
                                </div>
                                {viewMode === 'create' && (
                                    <button
                                        type="button"
                                        onClick={handleClearDraft}
                                        className="text-xs text-slate-400 hover:text-rose-600 font-semibold transition"
                                    >
                                        Svuota Campi
                                    </button>
                                )}
                            </div>

                            {/* SEZIONE 1: INFORMAZIONI GENERALI & ORGANIZZATIVE */}
                            <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200 space-y-4">
                                <h4 className="text-xs font-black uppercase tracking-wider text-[#24485d] flex items-center gap-1.5">
                                    <span>🏢</span>
                                    <span>1. Tipologia & Informazioni Generali</span>
                                </h4>

                                {/* SELETTORE TIPO: GRUPPO CONTINUATIVO vs INCONTRO SINGOLO */}
                                <div className="flex items-center gap-2 p-1.5 bg-white rounded-2xl border border-slate-200/90 shadow-xs">
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            meetingCategory: 'group', 
                                            isSingleMeeting: false,
                                            title: (formData.title === 'Incontro Conoscitivo' || formData.title === 'Incontro Singolo') ? '' : formData.title,
                                            sessionNumber: formData.sessionNumber === 'Incontro Singolo' ? '' : formData.sessionNumber
                                        })}
                                        className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 ${
                                            formData.meetingCategory !== 'single'
                                                ? 'bg-[#24485d] text-white shadow-sm'
                                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                        }`}
                                    >
                                        <span>👥</span>
                                        <span>Percorso di Gruppo (Continuativo)</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ 
                                            ...formData, 
                                            meetingCategory: 'single', 
                                            isSingleMeeting: true,
                                            title: formData.title || 'Incontro Conoscitivo',
                                            sessionNumber: 'Incontro Singolo'
                                        })}
                                        className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 ${
                                            formData.meetingCategory === 'single'
                                                ? 'bg-[#24485d] text-white shadow-sm'
                                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                        }`}
                                    >
                                        <span>🤝</span>
                                        <span>Incontro Singolo / Conoscitivo (Senza Gruppo)</span>
                                    </button>
                                </div>

                                <div className="grid sm:grid-cols-3 gap-3">
                                    <div className={formData.meetingCategory === 'single' ? "sm:col-span-3" : "sm:col-span-2"}>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            {formData.meetingCategory === 'single' ? 'Titolo / Tipo Incontro *' : 'Nome Gruppo / Percorso *'}
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            placeholder={formData.meetingCategory === 'single' ? "Es. Incontro Conoscitivo, Open Day, Workshop Esperienziale..." : "Es. Cerchio di Consapevolezza del Giovedì, Gruppo Gestalt..."}
                                            value={formData.title}
                                            onChange={e => setFormData({ ...formData, title: e.target.value })}
                                            className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                        />
                                        {formData.meetingCategory === 'single' && (
                                            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Suggerimenti:</span>
                                                {['Incontro Conoscitivo', 'Open Day / Presentazione', 'Workshop Esperienziale', 'Laboratorio Tematico', 'Cerchio Aperto'].map((sug, sIdx) => (
                                                    <button
                                                        key={sIdx}
                                                        type="button"
                                                        onClick={() => setFormData({ ...formData, title: sug })}
                                                        className="text-[11px] font-semibold bg-[#f0f5f8] hover:bg-[#e2edf2] text-[#24485d] border border-[#d1dde5] px-2 py-0.5 rounded-lg transition"
                                                    >
                                                        + {sug}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {formData.meetingCategory !== 'single' && (
                                        <div>
                                            <label className="block text-xs font-bold text-slate-700 mb-1">
                                                Sessione / N° Incontro
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Es. Incontro 3 di 8"
                                                value={formData.sessionNumber}
                                                onChange={e => setFormData({ ...formData, sessionNumber: e.target.value })}
                                                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="grid sm:grid-cols-3 gap-3">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            Data Incontro *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.date}
                                            onChange={e => setFormData({ ...formData, date: e.target.value })}
                                            className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            Durata / Orario
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Es. 90 min, 18:30 - 20:30"
                                            value={formData.duration}
                                            onChange={e => setFormData({ ...formData, duration: e.target.value })}
                                            className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            Luogo / Modalità 📍
                                        </label>
                                        <select
                                            value={PRESET_LOCATIONS.includes(formData.location) ? formData.location : 'Altro...'}
                                            onChange={e => {
                                                const val = e.target.value;
                                                if (val === 'Altro...') {
                                                    setFormData({ ...formData, location: 'Altro...' });
                                                } else {
                                                    setFormData({ ...formData, location: val, customLocation: '' });
                                                }
                                            }}
                                            className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                        >
                                            {PRESET_LOCATIONS.map(loc => (
                                                <option key={loc} value={loc}>{loc}</option>
                                            ))}
                                            <option value="Altro...">Altro (specifica sotto)...</option>
                                        </select>
                                    </div>
                                </div>

                                {formData.location === 'Altro...' && (
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            Specifica Luogo / Sede personalizzata
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Es. Centro Civico, Studio Via Garibaldi 12, Piattaforma Teams..."
                                            value={formData.customLocation}
                                            onChange={e => setFormData({ ...formData, customLocation: e.target.value })}
                                            className="w-full px-3.5 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                        />
                                    </div>
                                )}

                                <div className="grid sm:grid-cols-2 gap-3 pt-1">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            👤 Counselor Conduttore / Facilitatore *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Nome e cognome del Counselor conduttore"
                                            value={formData.counselorName}
                                            onChange={e => setFormData({ ...formData, counselorName: e.target.value })}
                                            className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            👥 Co-Conduttore / Osservatore (Opzionale)
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Es. Dott. Rossi (Tirocinante / Co-conduttore)"
                                            value={formData.coCounselor}
                                            onChange={e => setFormData({ ...formData, coCounselor: e.target.value })}
                                            className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                        />
                                    </div>
                                </div>

                                {/* SCELTA ICONA / AVATAR DEL GRUPPO */}
                                <div className="pt-2 border-t border-slate-200">
                                    <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center justify-between">
                                        <span>🖼️ Icona / Avatar del Gruppo</span>
                                        <span className="text-[11px] text-slate-500 font-normal">Selezionata: <strong className="text-base ml-1">{formData.groupAvatar || '👥'}</strong></span>
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {PRESET_AVATARS.map(item => (
                                            <button
                                                key={item.emoji}
                                                type="button"
                                                onClick={() => setFormData({ ...formData, groupAvatar: item.emoji })}
                                                className={`w-9 h-9 rounded-xl text-lg flex items-center justify-center transition border ${
                                                    (formData.groupAvatar || '👥') === item.emoji
                                                        ? 'bg-[#24485d] text-white border-[#1e3847] shadow ring-2 ring-[#335d73]/40'
                                                        : 'bg-white hover:bg-slate-100 border-slate-200 text-slate-700'
                                                }`}
                                                title={item.label}
                                            >
                                                {item.emoji}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* SEZIONE ENTE / AZIENDA */}
                                <div className="pt-2 border-t border-slate-200">
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={formData.isEntity}
                                            onChange={e => setFormData({ ...formData, isEntity: e.target.checked })}
                                            className="w-4 h-4 rounded text-[#24485d] focus:ring-[#335d73] border-slate-300"
                                        />
                                        <span className="text-xs font-bold text-slate-800">
                                            🏢 È commissionato da un Ente, Azienda o Associazione?
                                        </span>
                                    </label>

                                    {formData.isEntity && (
                                        <div className="mt-3 p-3.5 bg-amber-50/70 border border-amber-200 rounded-xl space-y-3 animate-fade-in">
                                            <div className="grid sm:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-[11px] font-bold text-amber-900 mb-1">
                                                        Nome Ente / Azienda *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        placeholder="Es. Fondazione Comunità, ACME Srl, Coop. Sociale..."
                                                        value={formData.entityName}
                                                        onChange={e => setFormData({ ...formData, entityName: e.target.value })}
                                                        className="w-full px-3 py-2 bg-white border border-amber-300 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-amber-900 mb-1">
                                                        Referente / Contatto Ente
                                                    </label>
                                                    <input
                                                        type="text"
                                                        placeholder="Es. Dott.ssa Bianchi (HR / Resp. Progetto)"
                                                        value={formData.entityReferent}
                                                        onChange={e => setFormData({ ...formData, entityReferent: e.target.value })}
                                                        className="w-full px-3 py-2 bg-white border border-amber-300 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-amber-900 mb-1">
                                                    Note Convenzione / Progetto Ente (Opzionale)
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder="Es. Convenzione welfare 2026, Percorso benessere 6 incontri"
                                                    value={formData.entityNotes}
                                                    onChange={e => setFormData({ ...formData, entityNotes: e.target.value })}
                                                    className="w-full px-3 py-2 bg-white border border-amber-300 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* OBIETTIVO GLOBALE DEL GRUPPO (PER IL REPORT DI GRUPPO) */}
                                {formData.meetingCategory !== 'single' && (
                                    <div className="pt-2 border-t border-slate-200">
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="block text-xs font-bold text-slate-800">
                                                🎯 Obiettivo Globale del Percorso di Gruppo (Opzionale)
                                            </label>
                                            <VoiceDictationButton
                                                label="Detta obiettivo percorso"
                                                currentValue={formData.groupObjective}
                                                onTranscript={(updater) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        groupObjective: typeof updater === 'function' ? updater(prev.groupObjective) : updater
                                                    }));
                                                }}
                                            />
                                        </div>
                                        <textarea
                                            rows="2"
                                            placeholder="Es. Esplorare e riconoscere il fenomeno dell'iperconnessione, promuovendo pratiche di disconnessione consapevole e ascolto autentico..."
                                            value={formData.groupObjective}
                                            onChange={e => setFormData({ ...formData, groupObjective: e.target.value })}
                                            className="w-full p-3 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                        />
                                        <p className="text-[11px] text-slate-500 mt-1">
                                            💡 Se non specificato, nel bilancio generale del gruppo verrà utilizzato automaticamente l'intento deontologico standard del counseling di gruppo.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* SEZIONE 2: PARTECIPANTI */}
                            <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200 space-y-3">
                                <div className="flex justify-between items-center">
                                    <h4 className="text-xs font-black uppercase tracking-wider text-[#24485d] flex items-center gap-1.5">
                                        <span>👥</span>
                                        <span>2. Partecipanti ({formData.selectedClientIds.length + (formData.externalParticipants ? formData.externalParticipants.split(',').filter(s => s.trim()).length : 0)})</span>
                                    </h4>
                                    <span className="text-[11px] text-slate-500 font-medium">
                                        Clicca per selezionare dall'anagrafica
                                    </span>
                                </div>

                                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-3 bg-white rounded-xl border border-slate-300">
                                    {clients.length === 0 ? (
                                        <span className="text-xs text-slate-400 italic">Nessun cliente presente in anagrafica.</span>
                                    ) : (
                                        clients.map(c => {
                                            const isSelected = formData.selectedClientIds.includes(c.id);
                                            return (
                                                <button
                                                    key={c.id}
                                                    type="button"
                                                    onClick={() => toggleClientSelection(c.id)}
                                                    className={`text-xs px-3 py-1.5 rounded-xl font-bold transition flex items-center gap-1.5 ${
                                                        isSelected
                                                            ? 'bg-[#335d73] text-white shadow-sm ring-2 ring-[#335d73]/30'
                                                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                                    }`}
                                                >
                                                    <span>{isSelected ? '✓' : '+'}</span>
                                                    <span>{c.name || c.email}</span>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>

                                <div className="grid sm:grid-cols-2 gap-3 pt-1">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            Ospiti / Partecipanti Esterni (separati da virgola)
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Es. Mario Rossi, Elena Bianchi..."
                                            value={formData.externalParticipants}
                                            onChange={e => setFormData({ ...formData, externalParticipants: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            Assenti Giustificati / Note Presenze
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Es. Marco N. (assente giustificato per lavoro)"
                                            value={formData.absentParticipants}
                                            onChange={e => setFormData({ ...formData, absentParticipants: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* SEZIONE 3: TEMA, OBIETTIVO & CLIMA DEL GRUPPO */}
                            <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200 space-y-4">
                                <h4 className="text-xs font-black uppercase tracking-wider text-[#24485d] flex items-center gap-1.5">
                                    <span>🎯</span>
                                    <span>3. Tema, Obiettivo & Clima Emotivo</span>
                                </h4>

                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="block text-xs font-bold text-slate-700">
                                            🎯 Tema dell'Incontro *
                                        </label>
                                        <VoiceDictationButton
                                            label="Detta tema"
                                            currentValue={formData.theme}
                                            onTranscript={(updater) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    theme: typeof updater === 'function' ? updater(prev.theme) : updater
                                                }));
                                            }}
                                        />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Es. I Confini Personali, L'Ombra e la Proiezione, La Fiducia e l'Esposizione..."
                                        value={formData.theme}
                                        onChange={e => setFormData({ ...formData, theme: e.target.value })}
                                        className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                    />
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="block text-xs font-bold text-slate-700">
                                            🎯 Obiettivo / Intento dell'Incontro
                                        </label>
                                        <VoiceDictationButton
                                            label="Detta obiettivo"
                                            currentValue={formData.objective}
                                            onTranscript={(updater) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    objective: typeof updater === 'function' ? updater(prev.objective) : updater
                                                }));
                                            }}
                                        />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Es. Favorire l'espressione dei bisogni inespressi e la differenziazione nel gruppo..."
                                        value={formData.objective}
                                        onChange={e => setFormData({ ...formData, objective: e.target.value })}
                                        className="w-full px-3.5 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                    />
                                </div>

                                {/* Clima Emotivo & Fase */}
                                <div className="space-y-3 pt-2">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1.5">
                                            🌡️ Clima Emotivo Prevalente
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {PRESET_CLIMATES.map(item => {
                                                const isSelected = formData.climate === item.label;
                                                return (
                                                    <button
                                                        key={item.label}
                                                        type="button"
                                                        onClick={() => setFormData({ ...formData, climate: isSelected ? '' : item.label })}
                                                        className={`text-xs px-3 py-1.5 rounded-xl font-bold transition flex items-center gap-1.5 border ${
                                                            isSelected
                                                                ? `${item.color} shadow-sm ring-2 ring-[#335d73]/40`
                                                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                                                        }`}
                                                    >
                                                        <span>{item.icon}</span>
                                                        <span>{item.label}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1.5">
                                            🔄 Fase Evolutiva del Gruppo
                                        </label>
                                        <div className="flex flex-wrap gap-1.5">
                                            {PRESET_PHASES.map(phase => {
                                                const isSelected = formData.groupPhase === phase;
                                                return (
                                                    <button
                                                        key={phase}
                                                        type="button"
                                                        onClick={() => setFormData({ ...formData, groupPhase: isSelected ? '' : phase })}
                                                        className={`text-[11px] px-2.5 py-1 rounded-lg font-bold transition border ${
                                                            isSelected
                                                                ? 'bg-[#24485d] text-white border-[#1e3847] shadow-sm'
                                                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                                                        }`}
                                                    >
                                                        {phase}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* SEZIONE 4: DINAMICHE & TEMI EMERSI */}
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-xs font-bold text-slate-800">
                                        ✨ Dinamiche Relazionali & Temi Emersi
                                    </label>
                                    <VoiceDictationButton
                                        label="Detta dinamiche"
                                        currentValue={formData.emergedThemes}
                                        onTranscript={(updater) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                emergedThemes: typeof updater === 'function' ? updater(prev.emergedThemes) : updater
                                            }));
                                        }}
                                    />
                                </div>
                                <textarea
                                    rows="5"
                                    placeholder="Dinamiche tra i membri, resistenze, alleanze, risonanze emotive, narrazioni significative condivise..."
                                    value={formData.emergedThemes}
                                    onChange={e => setFormData({ ...formData, emergedThemes: e.target.value })}
                                    className="w-full p-3.5 bg-slate-50 border border-slate-300 rounded-2xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73] leading-relaxed"
                                />
                            </div>

                            {/* SEZIONE 5: TECNICHE UTILIZZATE */}
                            <div>
                                <label className="block text-xs font-bold text-slate-800 mb-2">
                                    🛠️ Tecniche, Metodologie ed Esperimenti Utilizzati
                                </label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2.5">
                                    {PRESET_TECHNIQUES.map(tech => {
                                        const isSelected = formData.techniques.includes(tech.label);
                                        return (
                                            <button
                                                key={tech.id}
                                                type="button"
                                                onClick={() => toggleTechnique(tech.label)}
                                                className={`text-xs p-2.5 rounded-xl font-bold transition flex items-center gap-2 border text-left ${
                                                    isSelected
                                                        ? 'bg-[#335d73] text-white border-[#24485d] shadow-sm'
                                                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                                }`}
                                            >
                                                <span className="text-base">{tech.icon}</span>
                                                <span className="leading-tight text-[11px]">{tech.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <input
                                    type="text"
                                    placeholder="Altre tecniche / strumenti (es. Scultura simbolica, metafore guidate...)"
                                    value={formData.customTechnique}
                                    onChange={e => setFormData({ ...formData, customTechnique: e.target.value })}
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20"
                                />
                            </div>

                            {/* SEZIONE 6: INSIGHT & PUNTI DI SVOLTA */}
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-xs font-bold text-slate-800">
                                        💡 Insight & Punti di Svolta del Gruppo
                                    </label>
                                    <VoiceDictationButton
                                        label="Detta insight"
                                        currentValue={formData.insights}
                                        onTranscript={(updater) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                insights: typeof updater === 'function' ? updater(prev.insights) : updater
                                            }));
                                        }}
                                    />
                                </div>
                                <textarea
                                    rows="3"
                                    placeholder="Momenti 'Aha!', prese di consapevolezza collettive o individuali di particolare rilevanza..."
                                    value={formData.insights}
                                    onChange={e => setFormData({ ...formData, insights: e.target.value })}
                                    className="w-full p-3.5 bg-slate-50 border border-slate-300 rounded-2xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                />
                            </div>

                            {/* SEZIONE 7: ESERCIZI PER CASA (HOMEWORK) */}
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-xs font-bold text-slate-800">
                                        🏠 Eventuali Esercizi da Svolgere a Casa (Homework)
                                    </label>
                                    <VoiceDictationButton
                                        label="Detta homework"
                                        currentValue={formData.homework}
                                        onTranscript={(updater) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                homework: typeof updater === 'function' ? updater(prev.homework) : updater
                                            }));
                                        }}
                                    />
                                </div>
                                <textarea
                                    rows="3"
                                    placeholder="Pratiche quotidiane, diario di auto-osservazione, compiti espressivi da portare al prossimo incontro..."
                                    value={formData.homework}
                                    onChange={e => setFormData({ ...formData, homework: e.target.value })}
                                    className="w-full p-3.5 bg-slate-50 border border-slate-300 rounded-2xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20 focus:border-[#335d73]"
                                />
                            </div>

                            {/* SEZIONE 8: PROSSIMO INCONTRO */}
                            <div className="bg-slate-50/80 p-4 rounded-2xl border border-slate-200">
                                <label className="block text-xs font-bold text-slate-800 mb-2">
                                    📅 Prossimo Incontro / Follow-up
                                </label>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    <div>
                                        <input
                                            type="date"
                                            value={formData.nextSessionDate}
                                            onChange={e => setFormData({ ...formData, nextSessionDate: e.target.value })}
                                            className="w-full px-3.5 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20"
                                        />
                                    </div>
                                    <div>
                                        <input
                                            type="text"
                                            placeholder="Focus o tema previsto per il prossimo incontro"
                                            value={formData.nextSessionFocus}
                                            onChange={e => setFormData({ ...formData, nextSessionFocus: e.target.value })}
                                            className="w-full px-3.5 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#5c8aa8]/20"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* SEZIONE 9: NOTE E VISSUTO DEL COUNSELOR (RISERVATO) */}
                            <div className="bg-rose-50/40 p-5 rounded-2xl border border-rose-200/80 space-y-2">
                                <div className="flex justify-between items-center">
                                    <label className="block text-xs font-black uppercase tracking-wider text-rose-900 flex items-center gap-1.5">
                                        <span>🧘</span>
                                        <span>Note & Vissuto del Counselor (Riservato / Supervisione)</span>
                                    </label>
                                    <VoiceDictationButton
                                        label="Detta note"
                                        currentValue={formData.counselorNotes}
                                        onTranscript={(updater) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                counselorNotes: typeof updater === 'function' ? updater(prev.counselorNotes) : updater
                                            }));
                                        }}
                                    />
                                </div>
                                <textarea
                                    rows="3"
                                    placeholder="Risonanza interna del conduttore, difficoltà riscontrate, spunti da approfondire in supervisione..."
                                    value={formData.counselorNotes}
                                    onChange={e => setFormData({ ...formData, counselorNotes: e.target.value })}
                                    className="w-full p-3 bg-white border border-rose-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
                                />
                            </div>

                            {/* Form Actions */}
                            <div className="pt-4 border-t border-slate-200 flex justify-between items-center">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('list')}
                                    className="px-5 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 transition"
                                >
                                    Annulla
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="px-7 py-3 bg-[#335d73] hover:bg-[#24485d] text-white text-xs sm:text-sm font-black rounded-xl shadow-lg transition disabled:opacity-50 flex items-center gap-2"
                                >
                                    <span>💾</span>
                                    <span>{isSaving ? 'Salvataggio in corso...' : 'Salva Report di Gruppo'}</span>
                                </button>
                            </div>
                        </form>
                    )}

                    {/* VIEW: DETAIL / PRINT */}
                    {viewMode === 'detail' && selectedReport && (
                        <div className="max-w-4xl mx-auto space-y-4">
                            {/* Action Bar */}
                            <div className="flex flex-wrap justify-between items-center gap-3 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm print:hidden">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('list')}
                                    className="text-xs text-slate-700 hover:text-slate-950 font-bold flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition"
                                >
                                    <span>←</span>
                                    <span>Torna all'Elenco</span>
                                </button>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => handleCopyReport(selectedReport)}
                                        className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-800 transition flex items-center gap-1.5 border border-slate-200"
                                    >
                                        <span>{copiedId === selectedReport.id ? '✓' : '📋'}</span>
                                        <span>{copiedId === selectedReport.id ? 'Copiato!' : 'Copia Testo'}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDownloadPDF(selectedReport)}
                                        disabled={downloading}
                                        className="px-4 py-2 rounded-xl bg-[#335d73] hover:bg-[#24485d] text-xs font-black text-white transition flex items-center gap-1.5 shadow-md shadow-slate-900/20"
                                    >
                                        <span>🖨️</span>
                                        <span>{downloading ? 'Generazione PDF...' : 'Stampa / PDF'}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleOpenEdit(selectedReport)}
                                        className="px-3.5 py-2 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-xs font-bold text-indigo-700 transition border border-indigo-200 flex items-center gap-1"
                                    >
                                        <span>✏️</span>
                                        <span>Modifica</span>
                                    </button>
                                </div>
                            </div>

                            {/* Printable Sheet */}
                            <div
                                ref={printDocRef}
                                className="bg-white p-8 sm:p-12 rounded-3xl border border-slate-200 shadow-sm space-y-6 text-slate-800 font-sans print:p-0 print:border-none print:shadow-none print:max-w-none"
                            >
                                {/* Header */}
                                <div className="border-b-2 border-[#335d73] pb-5 flex flex-wrap justify-between items-start gap-4">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xl">👥</span>
                                            <span className="text-[11px] font-black text-[#335d73] uppercase tracking-widest">
                                                AwarenessTool • Counseling di Gruppo
                                            </span>
                                        </div>
                                        <h1 className="text-2xl sm:text-3xl font-black uppercase text-slate-900 tracking-tight">
                                            {selectedReport.title || 'Incontro di Gruppo'}
                                        </h1>
                                        {selectedReport.sessionNumber && (
                                            <span className="inline-block mt-1 bg-slate-100 text-slate-700 text-xs font-bold px-2.5 py-0.5 rounded-md border border-slate-200">
                                                {selectedReport.sessionNumber}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-left sm:text-right text-xs text-slate-600 space-y-0.5">
                                        <p><strong className="text-slate-800">Data:</strong> {new Date(selectedReport.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                        {selectedReport.duration && <p><strong className="text-slate-800">Durata:</strong> {selectedReport.duration}</p>}
                                        {selectedReport.location && <p><strong className="text-slate-800">Luogo:</strong> 📍 {selectedReport.location}</p>}
                                    </div>
                                </div>

                                {/* Meta Bar: Counselor & Conduttori */}
                                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 grid sm:grid-cols-2 gap-3 text-xs">
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Counselor Conduttore:</span>
                                        <span className="font-bold text-slate-800 text-sm">{selectedReport.counselorName || 'Non specificato'}</span>
                                    </div>
                                    {selectedReport.coCounselor && (
                                        <div>
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Co-Conduttore / Facilitatore:</span>
                                            <span className="font-bold text-slate-800 text-sm">{selectedReport.coCounselor}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Theme & Objective */}
                                {selectedReport.theme && (
                                    <div className="bg-[#f0f5f8] border border-[#d1dde5] p-5 rounded-2xl space-y-2">
                                        <div>
                                            <h4 className="text-[10px] font-black uppercase tracking-wider text-[#24485d] mb-0.5">
                                                🎯 Tema dell'Incontro
                                            </h4>
                                            <p className="text-base font-black text-[#1e293b]">
                                                {selectedReport.theme}
                                            </p>
                                        </div>
                                        {selectedReport.objective && (
                                            <div className="pt-2 border-t border-[#d1dde5]">
                                                <h5 className="text-[10px] font-bold uppercase tracking-wider text-[#335d73]">
                                                    Obiettivo dell'Incontro:
                                                </h5>
                                                <p className="text-xs text-[#24485d] font-medium">
                                                    {selectedReport.objective}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Climate & Phase */}
                                {(selectedReport.climate || selectedReport.groupPhase) && (
                                    <div className="flex flex-wrap gap-2">
                                        {selectedReport.climate && (
                                            <div className="bg-slate-100 text-slate-800 text-xs font-bold px-3 py-1.5 rounded-xl border border-slate-200 flex items-center gap-1.5">
                                                <span>🌡️ Clima Emotivo:</span>
                                                <span className="text-[#335d73]">{selectedReport.climate}</span>
                                            </div>
                                        )}
                                        {selectedReport.groupPhase && (
                                            <div className="bg-slate-100 text-slate-800 text-xs font-bold px-3 py-1.5 rounded-xl border border-slate-200 flex items-center gap-1.5">
                                                <span>🔄 Fase del Gruppo:</span>
                                                <span className="text-slate-900">{selectedReport.groupPhase}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Participants */}
                                <div>
                                    <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">
                                        👥 Partecipanti Presenti ({getParticipantNames(selectedReport).length})
                                    </h4>
                                    <div className="flex flex-wrap gap-1.5">
                                        {getParticipantNames(selectedReport).map((name, i) => (
                                            <span key={i} className="text-xs font-semibold bg-slate-100 text-slate-800 px-3 py-1 rounded-xl border border-slate-200">
                                                👤 {name}
                                            </span>
                                        ))}
                                    </div>
                                    {selectedReport.absentParticipants && (
                                        <p className="text-xs text-slate-500 mt-2 font-medium">
                                            <strong>Assenti / Note:</strong> {selectedReport.absentParticipants}
                                        </p>
                                    )}
                                </div>

                                {/* Emerged themes */}
                                {selectedReport.emergedThemes && (
                                    <div>
                                        <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">
                                            ✨ Dinamiche Relazionali & Temi Emersi
                                        </h4>
                                        <div className="text-xs sm:text-sm text-slate-800 leading-relaxed bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-200 whitespace-pre-wrap">
                                            {selectedReport.emergedThemes}
                                        </div>
                                    </div>
                                )}

                                {/* Techniques */}
                                {((selectedReport.techniques || []).length > 0 || selectedReport.customTechnique) && (
                                    <div>
                                        <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">
                                            🛠️ Tecniche e Strumenti Utilizzati
                                        </h4>
                                        <div className="flex flex-wrap gap-1.5">
                                            {[...(selectedReport.techniques || []), selectedReport.customTechnique].filter(Boolean).map((t, i) => (
                                                <span key={i} className="text-xs font-bold bg-[#f0f5f8] text-[#24485d] border border-[#d1dde5] px-3 py-1 rounded-xl">
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Insights */}
                                {selectedReport.insights && (
                                    <div>
                                        <h4 className="text-[10px] font-black uppercase tracking-wider text-amber-600 mb-1.5">
                                            💡 Insight & Punti di Svolta
                                        </h4>
                                        <div className="text-xs sm:text-sm text-amber-950 font-medium bg-amber-50/70 p-4 sm:p-5 rounded-2xl border border-amber-200 whitespace-pre-wrap">
                                            {selectedReport.insights}
                                        </div>
                                    </div>
                                )}

                                {/* Homework */}
                                {selectedReport.homework && (
                                    <div>
                                        <h4 className="text-[10px] font-black uppercase tracking-wider text-indigo-600 mb-1.5">
                                            🏠 Esercizi da Svolgere a Casa (Homework)
                                        </h4>
                                        <div className="text-xs sm:text-sm text-indigo-950 font-medium bg-indigo-50/70 p-4 sm:p-5 rounded-2xl border border-indigo-200 whitespace-pre-wrap">
                                            {selectedReport.homework}
                                        </div>
                                    </div>
                                )}

                                {/* Next Session */}
                                {(selectedReport.nextSessionDate || selectedReport.nextSessionFocus) && (
                                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                                        <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                                            📅 Prossimo Incontro
                                        </h4>
                                        <p className="text-xs text-slate-800 font-bold">
                                            {selectedReport.nextSessionDate && `Data: ${new Date(selectedReport.nextSessionDate).toLocaleDateString('it-IT')}`}
                                            {selectedReport.nextSessionFocus && ` — Focus: ${selectedReport.nextSessionFocus}`}
                                        </p>
                                    </div>
                                )}

                                {/* Counselor Notes */}
                                {selectedReport.counselorNotes && (
                                    <div className="bg-rose-50/50 p-5 rounded-2xl border border-rose-200 space-y-1.5">
                                        <h4 className="text-[10px] font-black uppercase tracking-wider text-rose-800 flex items-center gap-1.5">
                                            <span>🧘</span>
                                            <span>Note Riservate & Vissuto del Counselor (Supervisione)</span>
                                        </h4>
                                        <div className="text-xs sm:text-sm text-rose-950 italic whitespace-pre-wrap leading-relaxed">
                                            {selectedReport.counselorNotes}
                                        </div>
                                    </div>
                                )}

                                {/* Footer */}
                                <div className="pt-6 border-t border-slate-200 flex justify-between items-center text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                                    <span>CONFIDENZIALE - COUNSELING DI GRUPPO</span>
                                    <span>AwarenessTool Framework</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* MODALE REPORT GENERALE / BILANCIO DI GRUPPO */}
                {groupReportData && (
                    <div className="fixed inset-0 z-[60] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 overflow-y-auto animate-fade-in print:p-0 print:bg-white print:fixed">
                        <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-slate-200 print:max-h-none print:shadow-none print:border-none print:rounded-none">
                            {/* Header Modale Report */}
                            <div 
                                style={{ background: 'linear-gradient(135deg, #1e3847 0%, #24485d 100%)' }}
                                className="p-5 text-white flex items-center justify-between border-b border-[#335d73] print:hidden"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center text-2xl shadow-inner">
                                        {groupReportData.avatar || '👥'}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-lg font-black tracking-tight text-white">
                                                Bilancio & Report di Gruppo
                                            </h3>
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/20 border border-white/30 uppercase tracking-wider">
                                                {groupReportData.totalMeetings} {groupReportData.totalMeetings === 1 ? 'Incontro' : 'Incontri'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-200">
                                            {groupReportData.groupName}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleCopyGroupReport}
                                        className="px-3.5 py-2 rounded-xl bg-white/15 hover:bg-white/25 text-white text-xs font-bold transition flex items-center gap-1.5 border border-white/20"
                                    >
                                        <span>{copiedGroupReport ? '✓' : '📋'}</span>
                                        <span>{copiedGroupReport ? 'Copiato!' : 'Copia Testo'}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDownloadGroupReportPDF}
                                        disabled={downloading}
                                        className="px-4 py-2 rounded-xl bg-white hover:bg-slate-100 text-[#1e3847] text-xs font-black transition flex items-center gap-1.5 shadow"
                                    >
                                        <span>🖨️</span>
                                        <span>{downloading ? 'Generazione...' : 'Stampa / PDF'}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setGroupReportData(null)}
                                        className="w-9 h-9 rounded-xl text-white flex items-center justify-center transition border border-white/20 hover:bg-white/20 ml-1"
                                        title="Chiudi report"
                                    >
                                        ✕
                                    </button>
                                </div>
                            </div>

                            {/* Contenuto Stampabile / Esportabile */}
                            <div className="flex-1 overflow-y-auto p-6 sm:p-8 bg-slate-50/50 print:p-0 print:bg-white">
                                <div
                                    ref={groupReportDocRef}
                                    className="bg-white p-6 sm:p-10 rounded-3xl border border-slate-200 shadow-sm space-y-6 text-slate-800 font-sans print:p-0 print:border-none print:shadow-none print:max-w-none"
                                >
                                    {/* Intestazione Documento */}
                                    <div className="border-b-2 border-[#335d73] pb-5 flex flex-wrap justify-between items-start gap-4">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xl">{groupReportData.avatar || '👥'}</span>
                                                <span className="text-[11px] font-black text-[#335d73] uppercase tracking-widest">
                                                    AwarenessTool • Bilancio di Counseling di Gruppo
                                                </span>
                                            </div>
                                            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                                                {groupReportData.groupName}
                                            </h1>
                                            {groupReportData.entityInfo && (
                                                <div className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-900 text-xs font-bold px-3 py-1 rounded-xl border border-amber-200 mt-1">
                                                    <span>🏢 Ente Committente:</span>
                                                    <span>{groupReportData.entityInfo.entityName}</span>
                                                    {groupReportData.entityInfo.entityReferent && (
                                                        <span className="text-amber-700 font-normal">({groupReportData.entityInfo.entityReferent})</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className="text-left sm:text-right text-xs text-slate-600 space-y-1">
                                            <p className="bg-[#f0f5f8] text-[#24485d] font-bold px-3 py-1.5 rounded-xl border border-[#d1dde5] inline-block">
                                                📊 {groupReportData.totalMeetings} {groupReportData.totalMeetings === 1 ? 'incontro svolto' : 'incontri complessivi'}
                                            </p>
                                            <p className="text-[11px] text-slate-400">
                                                Generato il: {new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                                            </p>
                                        </div>
                                    </div>

                                    {/* OBIETTIVO DEL PERCORSO */}
                                    <div className="bg-[#f0f5f8] border border-[#d1dde5] p-5 rounded-2xl space-y-2">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-xs font-black uppercase tracking-wider text-[#24485d] flex items-center gap-1.5">
                                                <span>🎯</span>
                                                <span>Obiettivo del Percorso di Gruppo</span>
                                            </h3>
                                            {groupReportData.isDefaultObjective && (
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-white/70 px-2 py-0.5 rounded border border-[#d1dde5]">
                                                    Standard Deontologico
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs sm:text-sm text-slate-800 leading-relaxed font-medium">
                                            {groupReportData.objective}
                                        </p>
                                    </div>

                                    {/* PARTECIPAZIONE & PRESENZE DEI PARTECIPANTI */}
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-xs font-black uppercase tracking-wider text-[#24485d] flex items-center gap-1.5">
                                                <span>👥</span>
                                                <span>Presenze & Partecipazione Individuale ({groupReportData.participantsAttendance.length})</span>
                                            </h3>
                                            <span className="text-[11px] text-slate-500 font-semibold">
                                                Frequenza su {groupReportData.totalMeetings} {groupReportData.totalMeetings === 1 ? 'incontro' : 'incontri'}
                                            </span>
                                        </div>

                                        {groupReportData.participantsAttendance.length === 0 ? (
                                            <p className="text-xs text-slate-400 italic bg-slate-50 p-4 rounded-xl border border-slate-200">
                                                Nessun partecipante registrato negli incontri.
                                            </p>
                                        ) : (
                                            <div className="grid sm:grid-cols-2 gap-3">
                                                {groupReportData.participantsAttendance.map(p => (
                                                    <div 
                                                        key={p.name}
                                                        className="p-3.5 bg-slate-50/80 rounded-2xl border border-slate-200 flex flex-col justify-between gap-2"
                                                    >
                                                        <div className="flex items-center justify-between gap-2">
                                                            <div className="flex items-center gap-2">
                                                                <span className="w-7 h-7 rounded-xl bg-white border border-slate-200 text-slate-700 flex items-center justify-center text-xs font-bold shadow-xs">
                                                                    👤
                                                                </span>
                                                                <span className="text-xs font-bold text-slate-800">
                                                                    {p.name}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="text-xs font-black text-[#24485d] bg-[#f0f5f8] px-2 py-0.5 rounded-lg border border-[#d1dde5]">
                                                                    {p.attendedCount}/{p.total}
                                                                </span>
                                                                <span className={`text-[11px] font-black px-2 py-0.5 rounded-lg ${
                                                                    p.percentage >= 80 
                                                                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                                                                        : p.percentage >= 50 
                                                                            ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                                                            : 'bg-rose-50 text-rose-800 border border-rose-200'
                                                                }`}>
                                                                    {p.percentage}%
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Presenze per singolo incontro */}
                                                        <div className="flex items-center gap-1 pt-1.5 border-t border-slate-200/60 flex-wrap">
                                                            {p.meetingAttendance.map((att, attIdx) => (
                                                                <span
                                                                    key={attIdx}
                                                                    title={`${att.sessionNumber} (${att.date}): ${att.present ? 'Presente' : 'Assente'}`}
                                                                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 ${
                                                                        att.present 
                                                                            ? 'bg-emerald-100 text-emerald-900' 
                                                                            : 'bg-slate-200 text-slate-400 line-through'
                                                                    }`}
                                                                >
                                                                    <span>#{att.meetingIndex}</span>
                                                                    <span>{att.present ? '✓' : '✗'}</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* MOOD, CLIMA EMOTIVO & DINAMICHE GENERALI */}
                                    <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-3">
                                        <h3 className="text-xs font-black uppercase tracking-wider text-[#24485d] flex items-center gap-1.5">
                                            <span>🌡️</span>
                                            <span>Mood & Clima Generale del Percorso</span>
                                        </h3>
                                        
                                        <div className="flex flex-wrap gap-2">
                                            {groupReportData.climates.length > 0 ? (
                                                [...new Set(groupReportData.climates)].map((cl, cIdx) => (
                                                    <span key={cIdx} className="text-xs font-bold bg-white text-slate-800 px-3 py-1.5 rounded-xl border border-slate-200 shadow-xs flex items-center gap-1">
                                                        <span>🌡️</span>
                                                        <span>{cl}</span>
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-xs text-slate-400 italic">Clima emotivo non specificato negli incontri.</span>
                                            )}
                                        </div>

                                        {groupReportData.phases.length > 0 && (
                                            <div className="pt-2 border-t border-slate-200 text-xs text-slate-600">
                                                <strong className="text-slate-700">Fasi evolutive attraversate:</strong>{' '}
                                                {[...new Set(groupReportData.phases)].join(' → ')}
                                            </div>
                                        )}
                                    </div>

                                    {/* CRONOLOGIA E PUNTI SALIENTI DEGLI INCONTRI */}
                                    <div className="space-y-3">
                                        <h3 className="text-xs font-black uppercase tracking-wider text-[#24485d] flex items-center gap-1.5">
                                            <span>📅</span>
                                            <span>Sintesi Cronologica degli Incontri Svolti</span>
                                        </h3>

                                        <div className="space-y-3">
                                            {groupReportData.groupMeetings.map((m, idx) => {
                                                const dStr = new Date(m.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
                                                const mParts = getParticipantNames(m);

                                                return (
                                                    <div key={m.id || idx} className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2">
                                                        <div className="flex flex-wrap justify-between items-center gap-2 border-b border-slate-100 pb-2">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs font-black text-[#24485d] bg-[#f0f5f8] px-2.5 py-0.5 rounded-md border border-[#d1dde5]">
                                                                    Incontro #{idx + 1}
                                                                </span>
                                                                <span className="text-xs font-bold text-slate-700">
                                                                    📅 {dStr}
                                                                </span>
                                                            </div>
                                                            <span className="text-[11px] text-slate-500 font-medium">
                                                                Presenti: <strong>{mParts.length}</strong>
                                                            </span>
                                                        </div>

                                                        {m.theme && (
                                                            <p className="text-xs font-bold text-[#24485d]">
                                                                🎯 Tema: <span className="font-semibold text-slate-800">{m.theme}</span>
                                                            </p>
                                                        )}

                                                        {m.insights && (
                                                            <div className="text-xs bg-amber-50/70 text-amber-950 p-2.5 rounded-xl border border-amber-200">
                                                                <strong>💡 Insight / Punti di Svolta:</strong> {m.insights}
                                                            </div>
                                                        )}

                                                        {m.emergedThemes && (
                                                            <p className="text-xs text-slate-600 italic line-clamp-2">
                                                                "{m.emergedThemes}"
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="pt-6 border-t border-slate-200 flex justify-between items-center text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                                        <span>CONFIDENZIALE - COUNSELING DI GRUPPO</span>
                                        <span>AwarenessTool Framework</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODALE / POPOVER PER CAMBIARE AVATAR DEL GRUPPO */}
                {avatarPickerGroup && (
                    <div className="fixed inset-0 z-[70] bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fade-in">
                        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 border border-slate-200 space-y-4">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                                <div>
                                    <h4 className="text-base font-black text-slate-900">
                                        🖼️ Cambia Icona / Immagine
                                    </h4>
                                    <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[280px]">
                                        Gruppo: <strong>{avatarPickerGroup.groupName}</strong>
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setAvatarPickerGroup(null)}
                                    className="w-8 h-8 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center"
                                >
                                    ✕
                                </button>
                            </div>

                            <p className="text-xs text-slate-600">
                                Scegli l'icona da associare a questo gruppo di counseling:
                            </p>

                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2.5">
                                {PRESET_AVATARS.map(item => {
                                    const isSelected = avatarPickerGroup.currentAvatar === item.emoji;
                                    return (
                                        <button
                                            key={item.emoji}
                                            type="button"
                                            onClick={() => handleSelectGroupAvatar(avatarPickerGroup.groupName, item.emoji)}
                                            className={`h-14 rounded-2xl flex flex-col items-center justify-center transition border ${
                                                isSelected 
                                                    ? 'bg-[#24485d] text-white border-[#1e3847] ring-2 ring-[#335d73]/40 shadow' 
                                                    : 'bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-800'
                                            }`}
                                            title={item.label}
                                        >
                                            <span className="text-2xl">{item.emoji}</span>
                                            <span className="text-[9px] font-medium opacity-80 truncate max-w-[50px]">{item.label}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="pt-3 border-t border-slate-100 flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setAvatarPickerGroup(null)}
                                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition"
                                >
                                    Chiudi
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GroupCounselingModal;
